/**
 * Create Manual Invoice Page
 * Form: customer, invoice number (= reference), dates in one row, Vendor GSTIN from company profile, line items via modal only.
 */
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import {
  gstAppliesToDocumentFor,
  isPreGstRegistrationFor,
} from "../../../../shared/utils/gstApplicability";
import { GstPreRegistrationNotice } from "../../../../shared/presentation/components/GstPreRegistrationNotice";
import { LocationPicker } from "../../../../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../../../../shared/hooks/useActiveLocations";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Breadcrumbs } from "../../../../ui/components/Breadcrumbs";
import { TextField } from "../../../../ui/components/TextField";
import {
  SearchableDropdown,
  ErrorBanner,
  useToast,
} from "../../../../shared/presentation/components";
import { getCompany } from "../../../../shared/data/storage/authStorage";
import {
  isDocumentFxMismatch,
  parseExchangeRateInputForPayload,
  resolveCurrencyFieldsFromCatalog,
} from "../../../../shared/utils/manualDocumentCurrency";
import { isExchangeRateMissingForTallyPost } from "../../../../shared/utils/documentExchangeRate";
import { useZohoDocumentExchangeRate } from "../../../../shared/hooks/useZohoDocumentExchangeRate";
import { buildManualInvoiceJson } from "../../../../shared/utils/buildManualDocumentJson";
import { fetchCurrencies, type Currency } from "../../../../services/api";
import { handleError } from "../../../../utils/errorHandling";
import { useTaxes } from "../../../../shared/presentation/hooks/useTaxes";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import { createManualInvoiceDraft } from "../../data/api/invoicesApi";
import {
  InvoiceDataEntity,
  LineItemEntity,
} from "../../domain/entities/InvoiceEntity";
import { CreateManualInvoiceUseCase } from "../../domain/use-cases/invoices/CreateManualInvoiceUseCase";
import { InvoiceRepositoryImpl } from "../../data/repositories/InvoiceRepositoryImpl";
import EditLineItemManualInvoice from "../components/EditLineItemManualInvoice";
import SearchCustomerModal from "../components/SearchCustomerModal";
import { getAccountNameLeaf, getStateAbbreviation } from "../../../../utils";
import { extractErrorMessage } from "../../../../shared/utils/errorUtils";
import { formatDetectedLineItemGstDisplay } from "../../../../shared/utils/lineItemGstDisplay";
import {
  fetchTcsList,
  mapTcsDTOToEntity,
} from "../../../../shared/data/api/tcsApi";
import type { TcsEntity } from "../../../../shared/domain/entities/TaxEntity";
import {
  FeatherPlus,
  FeatherX,
  FeatherEdit2,
  FeatherChevronDown,
  FeatherUserPlus,
} from "@subframe/core";
import {
  buildManualInvoiceCustomerPatch,
  CREATE_MANUAL_INVOICE_ROUTE,
  type CustomerLikeForManualInvoice,
  type CreateManualInvoiceNavigationState,
} from "../utils/manualInvoiceCustomerPatch";
import {
  getCreateAndPushLabel,
  getStoredAccountingBackend,
} from "../../../../config/accounting";

const invoiceRepository = new InvoiceRepositoryImpl();
const createManualInvoiceUseCase = new CreateManualInvoiceUseCase(
  invoiceRepository,
);

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function defaultManualInvoiceData(): InvoiceDataEntity {
  const today = new Date().toISOString().slice(0, 10);
  const due = new Date();
  due.setDate(due.getDate() + 30);
  const dueDate = due.toISOString().slice(0, 10);

  return {
    customer_id: "",
    customer_name: "",
    customer_gstin: "",
    invoice_number: "",
    discount: 0,
    date: today,
    due_date: dueDate,
    reference_number: "",
    currency_id: "",
    currency_code: "INR",
    currency_symbol: "₹",
    vendor_name: "",
    vendor_gstin: "",
    gst_no: "",
    gst_treatment: "",
    source_of_supply: "",
    destination_of_supply: "",
    place_of_supply: "",
    invoice_date: today,
    eway_bill_number: "",
    irn_number: "",
    line_items: [],
    sub_total: 0,
    tax_total: 0,
    total: 0,
    payment_terms: 0,
    payment_terms_label: "",
    is_item_level_tax_calc: true,
    is_inclusive_tax: false,
    notes: "",
    terms: "",
    tcs_tax_id: "",
    tcs_tax_name: "",
    tcs_tax_percentage: undefined,
    tcs_nature_of_collection: "",
  };
}

const NO_TCS_ENTITY: TcsEntity = {
  id: 0,
  tcs_id: "",
  tcs_tax_name: "No TCS",
  tcs_tax_percentage: 0,
};

const CreateManualInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  const { getPermissionInfo, loading: permissionsLoading } = usePermissions();
  const company = getCompany();
  const accountingBackend = getStoredAccountingBackend();
  const isTallyBackend = accountingBackend === "tally";
  const createAndPushLabel = getCreateAndPushLabel(accountingBackend);
  const companyGstEnabled = company?.companyGstEnabled ?? 0;
  const createPermissionInfo = getPermissionInfo(
    MODULE_CODES.INVOICE,
    ACTION_CODES.CREATE,
  );
  const canCreate = createPermissionInfo.granted;
  const requiresReviewForCreate = createPermissionInfo.requiresReview;

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [draftIdForReview, setDraftIdForReview] = useState<number | null>(null);
  const [changeDetailsForReview, setChangeDetailsForReview] = useState<{
    raw_json: InvoiceDataEntity;
    action_types: string[];
  } | null>(null);

  const [invoiceData, setInvoiceData] = useState<InvoiceDataEntity>(
    defaultManualInvoiceData,
  );

  // ZOUR-19: does GST apply to the invoice BEING TYPED? Derived from
  // `invoiceData.date` so it re-evaluates as the date is edited — see
  // CreateManualBillPage for why this is resolved in the browser.
  // ZOUR-92: and on the registration picked above it — see the bill twin.
  const isGstEnabled = gstAppliesToDocumentFor(
    invoiceData.date || "",
    invoiceData.location_id ?? null,
    company
  );
  const showPreGstNotice = isPreGstRegistrationFor(
    invoiceData.date || "",
    invoiceData.location_id ?? null,
    company
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSearchCustomerModalOpen, setIsSearchCustomerModalOpen] =
    useState(false);
  const [isEditLineItemModalOpen, setIsEditLineItemModalOpen] = useState(false);
  const [editingLineItemData, setEditingLineItemData] =
    useState<LineItemEntity | null>(null);
  const [editingLineItemIndex, setEditingLineItemIndex] = useState<
    number | null
  >(null);
  const [originalAccountName, setOriginalAccountName] = useState("");
  const topRef = useRef<HTMLDivElement>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);
  const [exchangeRateInput, setExchangeRateInput] = useState("");

  const { taxes } = useTaxes();

  const showExchangeRateField = useMemo(
    () => isDocumentFxMismatch(invoiceData.currency_code),
    [invoiceData.currency_code],
  );
  /** Tally cannot look up a rate, so a foreign-currency invoice must carry one. */
  const exchangeRateRequired = isTallyBackend && showExchangeRateField;

  const { loading: exchangeRateLoading, error: exchangeRateFetchError } =
    useZohoDocumentExchangeRate({
      enabled: !isTallyBackend && showExchangeRateField,
      currencyId: invoiceData.currency_id,
      fromDate: invoiceData.date,
      currentRate: exchangeRateInput,
      onRateFetched: (rate) => {
        setExchangeRateInput(String(rate));
        setErrors((prev) => {
          if (!prev.exchange_rate) return prev;
          const next = { ...prev };
          delete next.exchange_rate;
          return next;
        });
      },
    });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        const res = await fetchCurrencies();
        if (!cancelled) setCurrencies(res.data);
      } catch (e) {
        if (!cancelled)
          setCurrenciesError(handleError(e, "currencies").message);
      } finally {
        if (!cancelled) setCurrenciesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** When API only gives `currency_id`, fill code/symbol from catalog. */
  useEffect(() => {
    const id = invoiceData.currency_id?.trim();
    if (!id || currencies.length === 0) return;
    const resolved = resolveCurrencyFieldsFromCatalog(currencies, id);
    if (!resolved) return;
    if (
      resolved.currency_code === invoiceData.currency_code &&
      resolved.currency_symbol === invoiceData.currency_symbol
    ) {
      return;
    }
    setInvoiceData((prev) => ({ ...prev, ...resolved }));
  }, [
    currencies,
    invoiceData.currency_id,
    invoiceData.currency_code,
    invoiceData.currency_symbol,
  ]);

  /** Default INR: set `currency_id` once catalog loads. */
  useEffect(() => {
    if (!currencies.length || invoiceData.currency_id?.trim()) return;
    const code = invoiceData.currency_code?.trim();
    if (!code) return;
    const hit = currencies.find((c) => c.currency_code === code);
    if (hit) {
      setInvoiceData((prev) => ({
        ...prev,
        currency_id: hit.currency_id,
        currency_symbol: hit.currency_symbol || prev.currency_symbol,
      }));
    }
  }, [currencies, invoiceData.currency_id, invoiceData.currency_code]);

  // ZOUR-65: which of our GST registrations is issuing this invoice.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
    primary: primaryLocation,
  } = useActiveLocations();

  /**
   * ZOUR-65 — our GSTIN and our place of supply come from the LOCATION.
   *
   * They used to come from `company.gstNumber` / `company.state`, which is the
   * right answer only while a company has one registration. An invoice issued
   * from the Bengaluru branch carries the Bengaluru GSTIN and is supplied from
   * Karnataka, and getting that wrong is not cosmetic — Zoho rejects the tax
   * group with code 3032, or worse, accepts it on the wrong registration.
   *
   * Re-runs when the picker changes, unlike the old one-shot prefill: switching
   * the location has to move both fields with it, or the reviewer is looking at
   * one branch's GSTIN under another branch's name.
   *
   * The company profile is still the fallback, and on a single-registration
   * tenant the synthetic primary IS the company profile, so nothing changes.
   */
  const selectedLocation = useMemo(
    () =>
      activeLocations.find(
        (location) => location.zoho_location_id === invoiceData.location_id
      ) ??
      primaryLocation ??
      null,
    [activeLocations, invoiceData.location_id, primaryLocation]
  );

  const hasPrefilledVendorGstin = useRef(false);
  useEffect(() => {
    const locationGstin = selectedLocation?.gstin ?? null;
    const gstin = locationGstin ?? company?.gstNumber ?? null;
    if (!gstin) return;

    // The first fill is one-shot as before; a later location change is not,
    // because it is an explicit instruction to move these fields.
    const locationChanged = hasPrefilledVendorGstin.current && !!locationGstin;
    if (hasPrefilledVendorGstin.current && !locationChanged) return;
    hasPrefilledVendorGstin.current = true;

    // state_code, never state: a synced row holds "Karnataka" in `state` and
    // "KA" in `state_code`, while the profile fallback holds "TN" in both.
    const vendorState =
      selectedLocation?.state_code ||
      getStateAbbreviation(company?.state || "") ||
      "";

    setInvoiceData((prev) => ({
      ...prev,
      vendor_gstin: gstin,
      gst_no: gstin,
      place_of_supply: vendorState || prev.place_of_supply,
    }));
  }, [
    company?.gstNumber,
    company?.state,
    selectedLocation?.gstin,
    selectedLocation?.state_code,
  ]);

  // Default the document to the primary registration so it always carries one.
  useEffect(() => {
    if (invoiceData.location_id || !primaryLocation?.zoho_location_id) return;
    setInvoiceData((prev) => ({
      ...prev,
      location_id: primaryLocation.zoho_location_id,
    }));
  }, [primaryLocation?.zoho_location_id, invoiceData.location_id]);

  const updateInvoiceJson = useCallback(
    (updates: Partial<InvoiceDataEntity>) => {
      setInvoiceData((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const fetchTcsItems = useCallback(
    async (search: string, limit: number, offset: number) => {
      const res = await fetchTcsList(search, limit, offset);
      const items = res.data.tcs.map(mapTcsDTOToEntity);
      if (offset === 0) {
        return {
          items: [NO_TCS_ENTITY, ...items],
          hasMore: res.data.pagination.has_more,
        };
      }
      return { items, hasMore: res.data.pagination.has_more };
    },
    [],
  );

  const handleTcsSelect = useCallback(
    (item: TcsEntity) => {
      if (item.tcs_id === "") {
        updateInvoiceJson({
          tcs_tax_id: "",
          tcs_tax_name: "",
          tcs_tax_percentage: undefined,
          tcs_nature_of_collection: "",
        });
      } else {
        updateInvoiceJson({
          tcs_tax_id: item.tcs_id,
          tcs_tax_name: item.tcs_tax_name,
          tcs_tax_percentage: item.tcs_tax_percentage,
          tcs_nature_of_collection: item.tcs_nature_of_collection ?? "",
        });
      }
    },
    [updateInvoiceJson],
  );

  const lineItems = useMemo(
    () => invoiceData.line_items || [],
    [invoiceData.line_items],
  );

  /**
   * ZOUR-65 — moving a manual invoice to another registration CLEARS its lines.
   *
   * Twin of the manual bill page, same reason: the registration decides the tax
   * family on every line (IGST vs CGST+SGST), a manual document has no
   * processing job to re-run, and remapping each line's tax id would be a guess.
   * Clearing is what the user chose (2026-09-16). Silent when there is nothing
   * to lose.
   */
  const [locationClearedNote, setLocationClearedNote] = useState<string | null>(
    null,
  );

  const handleLocationChange = useCallback(
    (locationId: string) => {
      setInvoiceData((prev) => {
        if ((prev.location_id ?? null) === locationId) return prev;

        const hadLines = (prev.line_items || []).length > 0;
        if (!hadLines) {
          setLocationClearedNote(null);
          return { ...prev, location_id: locationId };
        }

        const name =
          activeLocations.find(
            (candidate) => candidate.zoho_location_id === locationId,
          )?.name ?? "the new registration";

        setLocationClearedNote(
          `Registration changed to ${name}. Line items were cleared because the tax type (IGST vs CGST+SGST) depends on the registration — add them again.`,
        );

        return {
          ...prev,
          location_id: locationId,
          line_items: [],
          sub_total: 0,
          tax_total: 0,
          total: 0,
        };
      });
    },
    [activeLocations],
  );

  const hasSelectedCustomer = Boolean(invoiceData.customer_id?.trim());
  const customerNameDisplayText = !hasSelectedCustomer
    ? "No customer selected"
    : invoiceData.customer_name?.trim() || "";
  const customerAddressDisplayText = !hasSelectedCustomer
    ? "No customer selected"
    : invoiceData.customer_address?.trim()
      ? invoiceData.customer_address.trim()
      : "Customer has no address";
  const discount = invoiceData.discount ?? 0;

  const formatAmount = (amount: number): string =>
    (Number(amount) ?? 0).toFixed(2);
  const parseTaxRateFromName = (taxName: string): number | null => {
    const match = taxName?.match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : null;
  };
  const formatRate = (rate: number): string => {
    const fixed = Number(rate.toFixed(2));
    return Number.isInteger(fixed) ? fixed.toString() : fixed.toString();
  };
  const calculateTaxBreakdown = (
    items: LineItemEntity[],
    invData?: InvoiceDataEntity | null,
  ): { rows: { label: string; amount: number }[]; totalTax: number } => {
    const taxGroups: Record<
      string,
      { type: "IGST" | "GST"; rate: number; amount: number }
    > = {};
    const normalizeState = (state: string | null | undefined): string => {
      if (!state) return "";
      const normalized = state.toUpperCase().trim();
      if (normalized.length === 2) return normalized;
      try {
        return getStateAbbreviation(state) || normalized;
      } catch {
        return normalized;
      }
    };
    const placeOfSupply = normalizeState(invData?.place_of_supply);
    const destinationOfSupply = normalizeState(invData?.destination_of_supply);
    const hasSupplyInfo = placeOfSupply && destinationOfSupply;
    const isInterState = hasSupplyInfo && placeOfSupply !== destinationOfSupply;
    items.forEach((item) => {
      const taxName = (item.tax_name || "").toString();
      const taxPercentageRaw = item.tax_percentage;
      const taxRateFromPercentage =
        taxPercentageRaw !== undefined &&
        taxPercentageRaw !== null &&
        !isNaN(Number(taxPercentageRaw))
          ? parseFloat(String(taxPercentageRaw))
          : null;
      const parsedRateFromName = parseTaxRateFromName(taxName);
      const taxRate =
        taxRateFromPercentage !== null
          ? taxRateFromPercentage
          : parsedRateFromName !== null && !isNaN(parsedRateFromName)
            ? parsedRateFromName
            : 0;
      if (!taxRate) return;
      let taxType: "IGST" | "GST";
      const taxNameUpper = taxName.toUpperCase();
      if (taxNameUpper.includes("IGST")) taxType = "IGST";
      else if (taxNameUpper.includes("GST")) taxType = "GST";
      else if (hasSupplyInfo) taxType = isInterState ? "IGST" : "GST";
      else taxType = "GST";
      const taxableAmount = (item.rate || 0) * (item.quantity || 0);
      const taxAmount = (taxableAmount * taxRate) / 100;
      const key = `${taxType}-${taxRate}`;
      if (!taxGroups[key])
        taxGroups[key] = { type: taxType, rate: taxRate, amount: 0 };
      taxGroups[key].amount += taxAmount;
    });
    const rows: { label: string; amount: number }[] = [];
    Object.values(taxGroups).forEach((group) => {
      if (group.type === "IGST") {
        rows.push({
          label: `IGST ${formatRate(group.rate)}%`,
          amount: group.amount,
        });
      } else {
        const halfRate = group.rate / 2;
        const halfAmount = group.amount / 2;
        rows.push(
          { label: `SGST ${formatRate(halfRate)}%`, amount: halfAmount },
          { label: `CGST ${formatRate(halfRate)}%`, amount: halfAmount },
        );
      }
    });
    const totalTax = Object.values(taxGroups).reduce(
      (sum, g) => sum + g.amount,
      0,
    );
    return { rows, totalTax };
  };
  const taxBreakdown = useMemo(() => {
    return calculateTaxBreakdown(lineItems, invoiceData);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lineItems and supply fields drive breakdown
  }, [
    lineItems,
    invoiceData?.place_of_supply,
    invoiceData?.destination_of_supply,
  ]);

  const isInterState = useMemo(() => {
    const pos = (invoiceData?.place_of_supply ?? "")
      .toString()
      .trim()
      .toUpperCase();
    const dos = (invoiceData?.destination_of_supply ?? "")
      .toString()
      .trim()
      .toUpperCase();
    const posNorm =
      pos.length === 2
        ? pos
        : getStateAbbreviation(invoiceData?.place_of_supply ?? "") || pos;
    const dosNorm =
      dos.length === 2
        ? dos
        : getStateAbbreviation(invoiceData?.destination_of_supply ?? "") || dos;
    return !!posNorm && !!dosNorm && posNorm !== dosNorm;
  }, [invoiceData?.place_of_supply, invoiceData?.destination_of_supply]);

  const setLineItems = useCallback((items: LineItemEntity[]) => {
    const withOrder = items.map((item, i) => ({ ...item, item_order: i + 1 }));
    const subTotal = withOrder.reduce((sum, i) => sum + (i.item_total ?? 0), 0);
    const taxTotal = withOrder.reduce((sum, i) => sum + (i.tax_amount ?? 0), 0);
    setInvoiceData((prev) => ({
      ...prev,
      line_items: withOrder,
      sub_total: subTotal,
      tax_total: taxTotal,
      total: subTotal + taxTotal - (prev.discount ?? 0),
    }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleCustomerSelect = useCallback(
    async (customer: CustomerLikeForManualInvoice) => {
      const enriched: CustomerLikeForManualInvoice = { ...customer };
      const companyState = getStateAbbreviation(company?.state || "") || "";
      const patch = buildManualInvoiceCustomerPatch(
        enriched,
        companyState,
        currencies,
      );
      updateInvoiceJson(patch);
      setExchangeRateInput("");
      clearError("customer");
    },
    [updateInvoiceJson, clearError, company?.state, currencies],
  );

  const findCurrencyById = useCallback(
    (currencyId: string) =>
      currencies.find(
        (c) => String(c.currency_id).trim() === currencyId.trim(),
      ),
    [currencies],
  );

  const handleCurrencyDropdownSelect = useCallback(
    (c: Currency) => {
      updateInvoiceJson({
        currency_id: c.currency_id,
        currency_code: c.currency_code as InvoiceDataEntity["currency_code"],
        currency_symbol: c.currency_symbol,
      });
      setExchangeRateInput("");
      clearError("currency");
    },
    [updateInvoiceJson, clearError],
  );

  useEffect(() => {
    const st = location.state as CreateManualInvoiceNavigationState | null;
    if (!st || (st.invoiceDraft == null && st.applyCustomerPatch == null)) {
      return;
    }
    setInvoiceData((prev) => {
      const base = st.invoiceDraft ?? prev;
      return { ...base, ...(st.applyCustomerPatch || {}) };
    });
    if (st.applyCustomerPatch) clearError("customer");
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, clearError]);

  const handleAddLineItem = useCallback(() => {
    const newItem: LineItemEntity = {
      name: "",
      description: "",
      quantity: 0,
      rate: 0,
      unit: "nos",
      hsn_or_sac: "",
      hsn_description: "",
      sku: "",
      tax_id: "",
      tax_name: "",
      tax_percentage: 0,
      tax_amount: 0,
      account_id: "",
      account_name: "",
      item_total: 0,
      item_total_inclusive_of_tax: 0,
      item_order: lineItems.length + 1,
      is_billable: true,
    };
    setEditingLineItemIndex(-1);
    setEditingLineItemData(newItem);
    setOriginalAccountName("");
    setIsEditLineItemModalOpen(true);
  }, [lineItems.length]);

  const handleEditLineItem = useCallback(
    (index: number) => {
      setEditingLineItemIndex(index);
      const item = { ...lineItems[index] };
      if (item.tax_percentage === undefined || item.tax_percentage === null)
        item.tax_percentage = 0;
      setOriginalAccountName(item.account_name || "");
      setEditingLineItemData(item);
      setIsEditLineItemModalOpen(true);
    },
    [lineItems],
  );

  const handleDeleteLineItem = useCallback(
    (index: number) => {
      setLineItems(lineItems.filter((_, i) => i !== index));
    },
    [lineItems, setLineItems],
  );

  const handleSaveLineItem = useCallback(() => {
    if (!editingLineItemData) return;
    if (editingLineItemIndex === -1) {
      setLineItems([...lineItems, editingLineItemData]);
    } else if (editingLineItemIndex !== null) {
      const updated = [...lineItems];
      updated[editingLineItemIndex] = editingLineItemData;
      setLineItems(updated);
    }
    setIsEditLineItemModalOpen(false);
    setEditingLineItemData(null);
    setEditingLineItemIndex(null);
  }, [editingLineItemData, editingLineItemIndex, lineItems, setLineItems]);

  const handleLineItemDataChange = useCallback((data: LineItemEntity) => {
    setEditingLineItemData(data);
  }, []);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      const valid: File[] = [];
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_BYTES) continue;
        if (attachments.length + valid.length >= MAX_ATTACHMENTS) break;
        valid.push(file);
      }
      setAttachments((prev) => [...prev, ...valid].slice(0, MAX_ATTACHMENTS));
      e.target.value = "";
    },
    [attachments.length],
  );

  const handleRemoveFile = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {};
    if (!invoiceData.customer_id?.trim())
      next.customer = "Please select a customer.";
    if (!invoiceData.invoice_number?.trim())
      next.invoice_number = "Invoice number is required.";
    if (!invoiceData.currency_id?.trim())
      next.currency = "Please select a currency.";
    if (
      exchangeRateRequired &&
      isExchangeRateMissingForTallyPost(
        parseExchangeRateInputForPayload(exchangeRateInput),
        // A rate typed on this form is always deliberate, including a literal 1.
        { userSet: true, isPosted: false },
      )
    ) {
      next.exchange_rate =
        "Exchange rate is required for foreign currency documents when posting to Tally.";
    }
    if (!lineItems.length)
      next.line_items = "At least one line item is required.";
    if (
      isGstEnabled &&
      lineItems.some((i) => {
        const tid = String(i.tax_id ?? "").trim();
        const hasValidTax =
          tid.length > 0 || i.tax_id === "NO_TAX" || tid === "";
        return !hasValidTax;
      })
    ) {
      next.lineItemTax =
        "All line items must have a tax (or NO TAX) when GST is enabled.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [
    invoiceData.customer_id,
    invoiceData.invoice_number,
    invoiceData.currency_id,
    exchangeRateRequired,
    exchangeRateInput,
    lineItems,
    isGstEnabled,
  ]);

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true);
    if (exchangeRateLoading) {
      showError("Wait for the exchange rate to finish loading.");
      return;
    }
    if (!validate()) {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (!canCreate) {
      showError("You do not have permission to create invoices.");
      return;
    }
    setIsSubmitting(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.api;
      return next;
    });
    try {
      const built = buildManualInvoiceJson(invoiceData, lineItems, {
        currencies,
        companyGstEnabled: Number(companyGstEnabled) || 0,
        exchangeRateInput,
        // Zoho fills in the latest rate server-side; Tally has none to fall back on.
        requireExchangeRate: isTallyBackend,
      });
      if (!built.success) {
        setErrors((prev) => ({ ...prev, ...built.errors }));
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        setIsSubmitting(false);
        return;
      }
      const payload = built.data;

      if (requiresReviewForCreate) {
        const draftRes = await createManualInvoiceDraft(
          payload,
          Number(companyGstEnabled) || 0,
          attachments.length > 0 ? attachments : undefined,
        );
        const draftId = draftRes?.data?.id;
        if (!draftId) {
          throw new Error("Failed to create invoice draft");
        }
        setDraftIdForReview(draftId);
        setChangeDetailsForReview({
          raw_json: payload,
          action_types: ["create"],
        });
        setReviewModalOpen(true);
        setIsSubmitting(false);
        return;
      }
      const result = await createManualInvoiceUseCase.execute(
        payload,
        Number(companyGstEnabled) || 0,
        attachments.length > 0 ? attachments : undefined,
      );
      showSuccess("Invoice created successfully.");
      navigate("/invoices/review", {
        state: { documentId: String(result.id), isEdit: true },
      });
    } catch (err: any) {
      const message = extractErrorMessage(err) || "Failed to create invoice.";
      setErrors((prev) => ({ ...prev, api: message }));
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canCreate,
    requiresReviewForCreate,
    invoiceData,
    lineItems,
    currencies,
    exchangeRateInput,
    exchangeRateLoading,
    isTallyBackend,
    attachments,
    companyGstEnabled,
    validate,
    showError,
    showSuccess,
    navigate,
  ]);

  const handleReviewModalSuccess = useCallback(() => {
    setReviewModalOpen(false);
    setDraftIdForReview(null);
    setChangeDetailsForReview(null);
    navigate("/dashboard/invoices");
  }, [navigate]);

  if (permissionsLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-body font-body text-subtext-color">
            Loading...
          </span>
        </div>
      </DefaultPageLayout>
    );
  }

  if (!canCreate) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-body font-body text-subtext-color">
            You do not have permission to create invoices.
          </span>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div
        ref={topRef}
        className="mx-auto flex w-full max-w-7xl flex-col gap-8 bg-default-background px-6 py-6"
      >
        <Breadcrumbs>
          <Breadcrumbs.Item onClick={() => navigate("/dashboard")}>
            Dashboard
          </Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item onClick={() => navigate("/dashboard")}>
            Invoices
          </Breadcrumbs.Item>
          <Breadcrumbs.Divider />
          <Breadcrumbs.Item active>Create manual invoice</Breadcrumbs.Item>
        </Breadcrumbs>

        <form
          className="flex w-full flex-col gap-8"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {hasAttemptedSubmit && Object.keys(errors).length > 0 && (
            <ErrorBanner
              errors={errors}
              title="Please fix the following errors:"
              autoScrollOnMount={true}
              context="invoice"
            />
          )}
          <div className="flex w-full flex-col gap-6 rounded-lg border border-neutral-border bg-neutral-50 p-6">
            <h2 className="text-heading-2 font-heading-2 text-default-font">
              Invoice details
            </h2>
            <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-3 sm:col-span-2">
                <div className="flex w-full items-center justify-between gap-4">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Customer <span className="text-error-600">*</span>
                  </span>
                  <Button
                    type="button"
                    size="large"
                    icon={<FeatherUserPlus />}
                    onClick={() => setIsSearchCustomerModalOpen(true)}
                  >
                    Search Customer
                  </Button>
                </div>
                <div className="w-full items-start gap-4 grid grid-cols-1 sm:grid-cols-2">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <span className="text-body font-body text-subtext-color">
                      Customer name
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {customerNameDisplayText}
                    </span>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <span className="text-body font-body text-subtext-color">
                      Customer address
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font break-words">
                      {customerAddressDisplayText}
                    </span>
                  </div>
                </div>
                {errors.customer && (
                  <span className="text-caption font-caption text-error-600">
                    {errors.customer}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <TextField
                  label={
                    <>
                      Invoice number <span className="text-error-600">*</span>
                    </>
                  }
                >
                  <TextField.Input
                    placeholder=""
                    value={invoiceData.invoice_number || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      updateInvoiceJson({ invoice_number: e.target.value });
                      if (errors.invoice_number) clearError("invoice_number");
                    }}
                  />
                </TextField>
                {errors.invoice_number && (
                  <span className="text-caption font-caption text-error-600">
                    {errors.invoice_number}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <TextField label="Reference number">
                  <TextField.Input
                    placeholder=""
                    value={invoiceData.reference_number || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateInvoiceJson({ reference_number: e.target.value })
                    }
                  />
                </TextField>
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <div className="flex flex-row flex-wrap items-start gap-4 sm:gap-6">
                  <div className="flex min-w-[min(100%,12rem)] flex-1 flex-col gap-1.5">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      Currency <span className="text-error-600">*</span>
                    </span>
                    <SearchableDropdown<Currency>
                      items={currencies}
                      loading={currenciesLoading}
                      trigger={
                        <Button
                          type="button"
                          variant="neutral-secondary"
                          className="w-full max-w-full justify-between px-3 py-0"
                          iconRight={<FeatherChevronDown />}
                          disabled={currenciesLoading}
                        >
                          {currenciesLoading
                            ? "Loading currencies…"
                            : invoiceData.currency_id
                              ? findCurrencyById(invoiceData.currency_id)
                                  ?.currency_name_formatted || "Select currency"
                              : "Select currency"}
                        </Button>
                      }
                      getSearchableText={(c) =>
                        c.currency_name_formatted || c.currency_name
                      }
                      renderItem={(c) =>
                        c.currency_name_formatted || c.currency_name
                      }
                      getItemKey={(c) => c.currency_id}
                      onSelect={handleCurrencyDropdownSelect}
                      selectedKey={invoiceData.currency_id}
                      searchPlaceholder="Search currencies…"
                      emptyMessage="No currencies available"
                      noResultsMessage="No currencies match your search"
                    />
                    {currenciesError && (
                      <span className="text-caption font-caption text-error-600">
                        {currenciesError}
                      </span>
                    )}
                    {errors.currency && (
                      <span className="text-caption font-caption text-error-600">
                        {errors.currency}
                      </span>
                    )}
                  </div>
                  {showExchangeRateField ? (
                    <div className="flex min-w-[min(100%,10rem)] flex-1 flex-col gap-1.5">
                      <TextField
                        label={
                          exchangeRateRequired ? (
                            <>
                              Exchange rate{" "}
                              <span className="text-error-600">*</span>
                            </>
                          ) : (
                            "Exchange rate"
                          )
                        }
                        helpText={
                          exchangeRateLoading
                            ? "Fetching the Zoho Books rate for this document date…"
                            : exchangeRateRequired
                              ? "Required for Tally posting. Enter the rate relative to your company base currency."
                              : "Leave empty for latest rate."
                        }
                        className="w-full"
                      >
                        <TextField.Input
                          type="number"
                          step="any"
                          inputMode="decimal"
                          placeholder={
                            exchangeRateLoading
                              ? "Loading…"
                              : exchangeRateRequired
                                ? "Required"
                                : "Optional"
                          }
                          value={
                            exchangeRateLoading ? "" : exchangeRateInput
                          }
                          disabled={exchangeRateLoading}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setExchangeRateInput(e.target.value);
                            clearError("exchange_rate");
                          }}
                          onWheel={(e) => e.preventDefault()}
                        />
                      </TextField>
                      {(errors.exchange_rate || exchangeRateFetchError) && (
                        <span className="text-caption font-caption text-error-600">
                          {errors.exchange_rate || exchangeRateFetchError}
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2 sm:flex-row sm:gap-6">
                <TextField
                  label={
                    <>
                      Date <span className="text-error-600">*</span>
                    </>
                  }
                  className="min-w-0 flex-1 sm:min-w-[200px]"
                >
                  <TextField.Input
                    className="h-10 w-full"
                    type="date"
                    value={invoiceData.date}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateInvoiceJson({
                        date: e.target.value,
                        invoice_date: e.target.value,
                      })
                    }
                  />
                </TextField>
                <TextField
                  label={
                    <>
                      Due date <span className="text-error-600">*</span>
                    </>
                  }
                  className="min-w-0 flex-1 sm:min-w-[200px]"
                >
                  <TextField.Input
                    className="h-10 w-full"
                    type="date"
                    value={invoiceData.due_date}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateInvoiceJson({ due_date: e.target.value })
                    }
                  />
                </TextField>
              </div>
              {/* ZOUR-19: appears the moment the date is moved before the
                  registration date, so the tax columns vanishing is explained
                  as it happens rather than discovered later. */}
              {showPreGstNotice && (
                <div className="sm:col-span-2">
                  <GstPreRegistrationNotice
                    documentDate={invoiceData.date || ""}
                    documentLabel="invoice"
                  />
                </div>
              )}

              {/* ZOUR-65: which of our GST registrations issues this invoice.
                  Above the GSTIN field because it is what fills it. Hidden
                  entirely on a single-registration company. */}
              {hasMultipleLocations && (
                <div className="sm:col-span-2">
                  <LocationPicker
                    locations={activeLocations}
                    value={invoiceData.location_id ?? null}
                    onChange={handleLocationChange}
                  />
                </div>
              )}

              <TextField label="Vendor GSTIN">
                <TextField.Input
                  className="h-10"
                  placeholder="From company profile"
                  value={invoiceData.vendor_gstin ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateInvoiceJson({
                      vendor_gstin: e.target.value,
                      gst_no: e.target.value,
                    })
                  }
                />
              </TextField>
              {isGstEnabled && (
                <TextField label="Customer GSTIN">
                  <TextField.Input
                    className="h-10"
                    placeholder="From customer"
                    value={invoiceData.customer_gstin || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateInvoiceJson({ customer_gstin: e.target.value })
                    }
                  />
                </TextField>
              )}
              <div className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-caption-bold font-caption-bold text-default-font">
                  Notes
                </span>
                <textarea
                  className="min-h-[80px] w-full resize-y rounded-md border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font outline-none focus:border-brand-primary"
                  placeholder=""
                  value={invoiceData.notes || ""}
                  onChange={(e) => updateInvoiceJson({ notes: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-4 rounded-lg border border-neutral-border bg-neutral-50 p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-heading-2 font-heading-2 text-default-font">
                Line Items <span className="text-error-600">*</span>
              </h2>
              <Button
                type="button"
                variant="neutral-primary"
                size="small"
                icon={<FeatherPlus />}
                onClick={handleAddLineItem}
              >
                Add line item
              </Button>
            </div>

            {/* Non-blocking: reports what already happened to the lines. */}
            {locationClearedNote && (
              <div className="flex w-full items-start justify-between gap-3 rounded-md border border-solid border-warning-200 bg-warning-50 px-4 py-3">
                <span className="text-body font-body text-warning-800">
                  {locationClearedNote}
                </span>
                <button
                  type="button"
                  className="shrink-0 cursor-pointer text-caption font-caption text-warning-800 underline"
                  onClick={() => setLocationClearedNote(null)}
                >
                  Dismiss
                </button>
              </div>
            )}

            {lineItems.length === 0 ? (
              <div className="rounded-md border border-dashed border-neutral-border bg-neutral-50 py-8 text-center">
                <p className="text-body font-body text-subtext-color">
                  No line items yet. Click "Add line item" to add one.
                </p>
              </div>
            ) : (
              <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border overflow-x-auto">
                <div className="flex items-center gap-2 sm:gap-4 border-b border-solid border-neutral-border bg-default-background px-2 sm:px-4 py-3 w-full text-caption-bold font-caption-bold text-subtext-color">
                  <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Description
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Debit A/c
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Credit A/c
                    </span>
                  </div>
                  <div className="flex min-w-0 w-[60px] sm:w-[80px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      HSN Code
                    </span>
                  </div>
                  <div className="flex min-w-0 w-[40px] sm:w-[64px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Qty
                    </span>
                  </div>
                  <div className="flex min-w-0 w-[60px] sm:w-[80px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Rate
                    </span>
                  </div>
                  {isGstEnabled && (
                    <div className="flex min-w-0 w-[40px] sm:w-[64px] shrink-0 items-start">
                      <span className="text-body font-body text-default-font truncate block max-w-full">
                        GST
                      </span>
                    </div>
                  )}
                  <div className="flex min-w-0 w-[60px] sm:w-[96px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate block max-w-full">
                      Amount
                    </span>
                  </div>
                  <div className="flex min-w-[120px] shrink-0 items-start">
                    <span className="text-body font-body text-default-font truncate">
                      Actions
                    </span>
                  </div>
                </div>
                {lineItems.map((item, index) => {
                  const gstLineLabel = formatDetectedLineItemGstDisplay(item);
                  return (
                    <div
                      key={`${item.name ?? ""}-${index}`}
                      className="flex items-start gap-2 sm:gap-4 border-b border-solid border-neutral-100 bg-white px-2 sm:px-4 py-3 w-full"
                    >
                      <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-start">
                        <span
                          className="text-body-bold font-body-bold text-default-font truncate block max-w-full"
                          title={item.description || item.name || "—"}
                        >
                          {item.description || item.name || "—"}
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-center">
                        <span
                          className="text-caption font-caption text-default-font truncate block max-w-full"
                          title="Account Receivable"
                        >
                          Account Receivable
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-1 min-w-[90px] shrink-0 items-center">
                        <span
                          className="text-caption font-caption text-default-font truncate block max-w-full"
                          title={getAccountNameLeaf(item.account_name) || "—"}
                        >
                          {getAccountNameLeaf(item.account_name) || "—"}
                        </span>
                      </div>
                      <div className="flex min-w-0 w-[60px] sm:w-[80px] shrink-0 items-center">
                        <span
                          className="text-body font-body text-default-font truncate block max-w-full"
                          title={item.hsn_or_sac || "N/A"}
                        >
                          {item.hsn_or_sac || "N/A"}
                        </span>
                      </div>
                      <div className="flex min-w-0 w-[40px] sm:w-[64px] shrink-0 items-center">
                        <span
                          className="text-body font-body text-default-font truncate block max-w-full"
                          title={String(item.quantity ?? 0)}
                        >
                          {item.quantity ?? 0}
                        </span>
                      </div>
                      <div className="flex min-w-0 w-[60px] sm:w-[80px] shrink-0 items-center">
                        <span
                          className="text-body font-body text-default-font truncate block max-w-full"
                          title={`${invoiceData.currency_symbol || "₹"}${formatAmount(item.rate ?? 0)}`}
                        >
                          {invoiceData.currency_symbol || "₹"}
                          {formatAmount(item.rate ?? 0)}
                        </span>
                      </div>
                      {isGstEnabled && (
                        <div className="flex min-w-0 w-[40px] sm:w-[64px] shrink-0 items-center">
                          <span
                            className="text-body font-body text-default-font truncate block max-w-full"
                            title={gstLineLabel}
                          >
                            {gstLineLabel}
                          </span>
                        </div>
                      )}
                      <div className="flex min-w-0 w-[60px] sm:w-[96px] shrink-0 items-center">
                        <span
                          className="text-body-bold font-body-bold text-default-font truncate block max-w-full"
                          title={`${invoiceData.currency_symbol || "₹"}${formatAmount(
                            isGstEnabled
                              ? (item.rate ?? 0) * (item.quantity ?? 0) +
                                  ((item.rate ?? 0) *
                                    (item.quantity ?? 0) *
                                    (item.tax_percentage ?? 0)) /
                                    100
                              : (item.rate ?? 0) * (item.quantity ?? 0),
                          )}`}
                        >
                          {invoiceData.currency_symbol || "₹"}
                          {formatAmount(
                            isGstEnabled
                              ? (item.rate ?? 0) * (item.quantity ?? 0) +
                                  ((item.rate ?? 0) *
                                    (item.quantity ?? 0) *
                                    (item.tax_percentage ?? 0)) /
                                    100
                              : (item.rate ?? 0) * (item.quantity ?? 0),
                          )}
                        </span>
                      </div>
                      <div className="flex min-w-[120px] shrink-0 items-center gap-2">
                        <Button
                          type="button"
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherEdit2 />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditLineItem(index);
                          }}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          icon={<FeatherX />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLineItem(index);
                          }}
                          aria-label="Remove line"
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="flex flex-col items-end gap-3 border-t border-solid border-neutral-200 bg-default-background px-4 py-4 min-w-full">
                  <div className="flex w-full max-w-[384px] items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Subtotal
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {invoiceData.currency_symbol || "₹"}
                      {formatAmount(
                        lineItems.reduce(
                          (sum, item) =>
                            sum + (item.rate ?? 0) * (item.quantity ?? 0),
                          0,
                        ),
                      )}
                    </span>
                  </div>
                  {discount !== 0 && (
                    <div className="flex w-full max-w-[384px] items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Discount/Adjustments
                      </span>
                      <span
                        className={`text-body-bold font-body-bold ${
                          discount < 0 ? "text-success-600" : "text-error-600"
                        }`}
                      >
                        {invoiceData.currency_symbol || "₹"}
                        {formatAmount(discount)}
                      </span>
                    </div>
                  )}
                  {isGstEnabled &&
                    taxBreakdown.rows.map((row) => (
                      <div
                        key={row.label}
                        className="flex w-full max-w-[384px] items-center justify-between"
                      >
                        <span className="text-body font-body text-subtext-color">
                          {row.label}
                        </span>
                        <span className="text-body-bold font-body-bold text-default-font">
                          {invoiceData.currency_symbol || "₹"}
                          {formatAmount(row.amount)}
                        </span>
                      </div>
                    ))}
                  {isGstEnabled && (
                    <div className="flex w-full max-w-[384px] items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        TCS
                      </span>
                      <div className="w-full max-w-[220px] min-w-0 flex justify-end">
                        <SearchableDropdown<TcsEntity>
                          fetchItems={fetchTcsItems}
                          initialLimit={10}
                          maxHeight="max-h-60"
                          className="w-full"
                          dropdownClassName="min-w-[200px]"
                          trigger={
                            <Button
                              variant="neutral-secondary"
                              size="small"
                              iconRight={<FeatherChevronDown />}
                              className="w-full min-h-10 h-10 py-2 min-w-0 justify-between"
                            >
                              <span className="truncate min-w-0">
                                {invoiceData.tcs_tax_name &&
                                invoiceData.tcs_tax_id
                                  ? invoiceData.tcs_tax_name
                                  : "No TCS"}
                              </span>
                            </Button>
                          }
                          placeholder="Select TCS"
                          getSearchableText={(item) => item.tcs_tax_name}
                          renderItem={(item) =>
                            item.tcs_id === ""
                              ? item.tcs_tax_name
                              : `${item.tcs_tax_name} (${item.tcs_tax_percentage}%)`
                          }
                          getItemKey={(item) => item.tcs_id}
                          onSelect={handleTcsSelect}
                          selectedKey={invoiceData.tcs_tax_id ?? ""}
                        />
                      </div>
                    </div>
                  )}
                  {isGstEnabled &&
                    (() => {
                      const baseForTcs =
                        lineItems.reduce(
                          (sum, item) =>
                            sum + (item.rate ?? 0) * (item.quantity ?? 0),
                          0,
                        ) +
                        discount +
                        taxBreakdown.totalTax;
                      const tcsPct = invoiceData.tcs_tax_percentage ?? 0;
                      const tcsAmount =
                        invoiceData.tcs_tax_id && tcsPct > 0
                          ? baseForTcs * (tcsPct / 100)
                          : 0;
                      return tcsAmount > 0 ? (
                        <div className="flex w-full max-w-[384px] items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            TCS
                            {invoiceData.tcs_tax_name
                              ? ` (${invoiceData.tcs_tax_name})`
                              : ""}
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {invoiceData.currency_symbol || "₹"}
                            {formatAmount(tcsAmount)}
                          </span>
                        </div>
                      ) : null;
                    })()}
                  <div className="flex w-full max-w-[384px] items-center justify-between border-t border-solid border-neutral-border pt-3">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Total Amount
                    </span>
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      {invoiceData.currency_symbol || "₹"}
                      {formatAmount(
                        (() => {
                          const subtotal = lineItems.reduce(
                            (sum, item) =>
                              sum + (item.rate ?? 0) * (item.quantity ?? 0),
                            0,
                          );
                          const totalGst = isGstEnabled
                            ? taxBreakdown.totalTax
                            : 0;
                          const baseForTcs = subtotal + discount + totalGst;
                          const tcsPct = invoiceData.tcs_tax_percentage ?? 0;
                          const tcsAmount =
                            invoiceData.tcs_tax_id && tcsPct > 0
                              ? baseForTcs * (tcsPct / 100)
                              : 0;
                          return baseForTcs + tcsAmount;
                        })(),
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex w-full flex-col gap-4 rounded-lg border border-neutral-border bg-neutral-50 p-6">
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              Attachments
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="file"
                id="manual-invoice-file-upload"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept="*"
              />
              <Button
                type="button"
                variant="neutral-secondary"
                iconRight={<FeatherPlus />}
                disabled={attachments.length >= MAX_ATTACHMENTS}
                onClick={() =>
                  document.getElementById("manual-invoice-file-upload")?.click()
                }
              >
                Upload File
              </Button>
              <span className="text-caption font-caption text-subtext-color">
                Max {MAX_ATTACHMENTS} files, {MAX_FILE_SIZE_MB}MB each (
                {attachments.length}/{MAX_ATTACHMENTS})
              </span>
            </div>
            {attachments.length === 0 ? (
              <div className="rounded-md border border-dashed border-neutral-border bg-neutral-50 py-6 text-center">
                <span className="text-body font-body text-subtext-color">
                  No attachments
                </span>
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {attachments.map((file, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between rounded bg-white px-3 py-2 border border-neutral-100"
                  >
                    <span className="truncate text-body font-body text-default-font">
                      {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <IconButton
                      size="small"
                      icon={<FeatherX />}
                      onClick={() => handleRemoveFile(index)}
                      aria-label="Remove file"
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex w-full justify-end gap-3 border-t border-neutral-border pt-6">
            <Button
              type="button"
              variant="neutral-tertiary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              disabled={isSubmitting || exchangeRateLoading}
            >
              {isSubmitting
                ? requiresReviewForCreate
                  ? "Creating draft…"
                  : "Creating…"
                : exchangeRateLoading
                  ? "Loading exchange rate…"
                  : requiresReviewForCreate
                    ? "Submit for review"
                    : createAndPushLabel}
            </Button>
          </div>
        </form>

        <ReviewRequestModal
          open={reviewModalOpen}
          onOpenChange={setReviewModalOpen}
          moduleCode={MODULE_CODES.INVOICE}
          actionCode={ACTION_CODES.CREATE}
          entityType="invoice"
          entityId={draftIdForReview ?? 0}
          changeDetails={changeDetailsForReview ?? undefined}
          onSuccess={handleReviewModalSuccess}
          skipNavigation={true}
          title="Submit for review"
        />

        <SearchCustomerModal
          open={isSearchCustomerModalOpen}
          onOpenChange={setIsSearchCustomerModalOpen}
          onPartySelect={(party) => {
            void handleCustomerSelect(party as CustomerLikeForManualInvoice);
          }}
          initialSearchTerm={invoiceData.customer_name}
          initialSelectedCustomerId={invoiceData.customer_id || undefined}
          createManualInvoiceFlow={{
            returnPath: CREATE_MANUAL_INVOICE_ROUTE,
            invoiceDraft: invoiceData,
          }}
        />
      </div>

      <EditLineItemManualInvoice
        open={isEditLineItemModalOpen}
        onOpenChange={setIsEditLineItemModalOpen}
        editingLineItemData={editingLineItemData}
        originalAccountName={originalAccountName}
        editingLineItemIndex={editingLineItemIndex}
        taxes={taxes}
        customerName={invoiceData.customer_name}
        customerId={invoiceData.customer_id}
        isNewItem={editingLineItemIndex === -1}
        isGstEnabled={isGstEnabled}
        currencySymbol={invoiceData.currency_symbol}
        gstTreatment={invoiceData.gst_treatment}
        isInterState={isInterState}
        onSave={handleSaveLineItem}
        onCancel={() => {
          setIsEditLineItemModalOpen(false);
          setEditingLineItemData(null);
          setEditingLineItemIndex(null);
        }}
        onLineItemDataChange={handleLineItemDataChange}
      />
    </DefaultPageLayout>
  );
};

export default CreateManualInvoicePage;
